import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Container, Factory, Building2, ShoppingBag, Hotel, Sun, Truck, HeartPulse, Theater, ShieldCheck, TrainFront } from "lucide-react";
import { ScrollReveal } from "@/hooks/use-scroll-reveal";
import heroImg from "@/assets/hero-sez.jpg";
import dryPortImg from "@/assets/dry-port.jpg";
import financialImg from "@/assets/financial-center.jpg";
import solarImg from "@/assets/solar-plant.jpg";
import retailImg from "@/assets/retail-mall.jpg";
import hotelImg from "@/assets/hotel.jpg";
import manufacturingImg from "@/assets/manufacturing.jpg";
import logisticsImg from "@/assets/logistics.jpg";
import hospitalImg from "@/assets/hospital.jpg";
import conventionImg from "@/assets/convention-center.jpg";

// Only eagerly load the first clip; others load on demand
import clipSezOverview from "@/assets/clips/clip-sez-overview.mp4";

const clipModules = [
  () => clipSezOverview, // already imported
  () => import("@/assets/clips/clip-dry-port.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-logistics.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-manufacturing.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-financial.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-medical.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-hospitality.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-retail.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-convention.mp4").then(m => m.default),
  () => import("@/assets/clips/clip-solar.mp4").then(m => m.default),
];

const heroLabels = [
  "New Silkway Special Economic Zone",
  "Automated Inland Dry Port",
  "Integrated Logistics Center",
  "Advanced Manufacturing Park",
  "International Financial Center",
  "Medical & Research Campus",
  "Premium Hospitality District",
  "Duty-Free Retail & Shopping",
  "World-Class Convention Center",
  "100 MW Solar Energy Farm",
];

const useParallax = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        setOffset(ref.current.getBoundingClientRect().top * 0.4);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return { ref, offset };
};

const stats = [
  { value: "2,000+", label: "Hectares" },
  { value: "600 ha", label: "Dry Port" },
  { value: "8M", label: "TEU Capacity/Year" },
  { value: "300 ha", label: "Logistics Hub" },
  { value: "100 MW", label: "Solar Power" },
  { value: "10", label: "Hotels" },
];

const sectors = [
  { icon: Container, label: "Inland Dry Port", desc: "600 ha — 8M TEU capacity with dedicated rail corridor connecting Central Asia to global trade", path: "/development/dry-port", img: dryPortImg, featured: true },
  { icon: Truck, label: "Logistics Center", desc: "300 ha — Comprehensive warehousing, distribution, and supply chain infrastructure", path: "/development/logistics", img: logisticsImg },
  { icon: Factory, label: "Manufacturing Park", desc: "250 ha — World-class industrial facilities with SEZ tax and customs incentives", path: "/development/manufacturing", img: manufacturingImg },
  { icon: ShoppingBag, label: "Duty-Free Retail", desc: "150 ha — 1,400+ stores and 100+ restaurants in a premier shopping destination", path: "/development/retail", img: retailImg },
  { icon: Sun, label: "Sustainable Energy", desc: "150 ha — 100 MW solar power plant providing clean energy to the entire zone", path: "/development/energy", img: solarImg },
  { icon: Building2, label: "Global Financial Center", desc: "100 ha — 160,000 sqm of Grade A office space for international business", path: "/development/financial-center", img: financialImg },
  { icon: Hotel, label: "Hospitality", desc: "100 ha — 10 hotels from business-class to luxury five-star, spread across the zone", path: "/development/hospitality", img: hotelImg },
  { icon: HeartPulse, label: "Medical & Research", desc: "50 ha — 400-bed hospital and advanced medical research center", path: "/development/medical", img: hospitalImg },
  { icon: Theater, label: "Convention Center", desc: "World-class events, conferences, and entertainment venue", path: "/development/convention", img: conventionImg },
  { icon: ShieldCheck, label: "Customs Facilities", desc: "50 ha — Integrated customs clearance and bonded zone adjacent to the dry port", path: "/development/customs", img: dryPortImg },
];

const Index = () => {
  const hero = useParallax();
  const midSection = useParallax();
  const [visible, setVisible] = useState(false);
  const [clipIndex, setClipIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const videoRefA = useRef<HTMLVideoElement>(null);
  const videoRefB = useRef<HTMLVideoElement>(null);
  const [activeSlot, setActiveSlot] = useState<"A" | "B">("A");
  const clipCache = useRef<Record<number, string>>({ 0: clipSezOverview });

  const getClipSrc = useCallback(async (index: number): Promise<string> => {
    if (clipCache.current[index]) return clipCache.current[index];
    const src = await clipModules[index]();
    clipCache.current[index] = src;
    return src;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Preload the next clip into the inactive video element
  useEffect(() => {
    const nextIndex = (clipIndex + 1) % clipModules.length;
    const inactiveRef = activeSlot === "A" ? videoRefB : videoRefA;
    getClipSrc(nextIndex).then(src => {
      if (inactiveRef.current) {
        inactiveRef.current.src = src;
        inactiveRef.current.load();
      }
    });
  }, [clipIndex, activeSlot, getClipSrc]);

  const advanceClip = useCallback(() => {
    setFadeIn(false);
    setTimeout(() => {
      const nextIndex = (clipIndex + 1) % clipModules.length;
      setClipIndex(nextIndex);
      const nextSlot = activeSlot === "A" ? "B" : "A";
      const nextRef = nextSlot === "A" ? videoRefA : videoRefB;
      if (nextRef.current) {
        nextRef.current.play().catch(() => {});
      }
      setActiveSlot(nextSlot);
      setFadeIn(true);
    }, 400);
  }, [clipIndex, activeSlot]);

  useEffect(() => {
    const activeRef = activeSlot === "A" ? videoRefA : videoRefB;
    const videoEl = activeRef.current;
    if (!videoEl) return;
    const handleEnded = () => advanceClip();
    videoEl.addEventListener("ended", handleEnded);
    return () => videoEl.removeEventListener("ended", handleEnded);
  }, [activeSlot, advanceClip]);

  return (
    <div>
      {/* Hero */}
      <div ref={hero.ref} className="relative h-screen overflow-hidden flex items-center">
        {/* Video layer A */}
        <video
          ref={videoRefA}
          autoPlay
          muted
          playsInline
          preload="auto"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${activeSlot === "A" && fadeIn ? "opacity-100" : "opacity-0"}`}
          style={{ transform: `translateY(${hero.offset}px)`, willChange: "transform" }}
          // @ts-ignore
          fetchpriority="high"
        >
          <source src={clipSezOverview} type="video/mp4" />
        </video>
        {/* Video layer B */}
        <video
          ref={videoRefB}
          muted
          playsInline
          preload="auto"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${activeSlot === "B" && fadeIn ? "opacity-100" : "opacity-0"}`}
          style={{ transform: `translateY(${hero.offset}px)`, willChange: "transform" }}
        />
        <div className="absolute inset-0 overlay-gradient" />

        {/* Segment name label */}
        <div className="absolute bottom-24 left-6 md:left-12 lg:left-20 z-20">
          <p
            className={`text-sm md:text-base font-display tracking-[0.2em] uppercase text-foreground/90 transition-all duration-700 ${fadeIn ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.6)" }}
          >
            {heroLabels[clipIndex]}
          </p>
        </div>

        {/* Clip dot indicators */}
        <div className="absolute top-6 right-6 z-20 flex gap-2">
           {heroLabels.map((label, i) => (
             <button
               key={i}
               onClick={() => {
                 if (i === clipIndex) return;
                 const nextSlot = activeSlot === "A" ? "B" : "A";
                 const nextRef = nextSlot === "A" ? videoRefA : videoRefB;
                 getClipSrc(i).then(src => {
                   if (nextRef.current) {
                     nextRef.current.src = src;
                     nextRef.current.load();
                   }
                   setFadeIn(false);
                   setTimeout(() => {
                     setClipIndex(i);
                     if (nextRef.current) {
                       nextRef.current.play().catch(() => {});
                     }
                     setActiveSlot(nextSlot);
                     setFadeIn(true);
                   }, 400);
                 });
               }}
               className="p-2 transition-all duration-300"
               aria-label={label}
             >
               <span className={`block w-2 h-2 rounded-full transition-all duration-300 ${i === clipIndex ? "bg-primary w-6" : "bg-foreground/30 hover:bg-foreground/50"}`} />
             </button>
          ))}
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto w-full">
          <div className={`max-w-2xl transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
              Introducing the New Silkway<br />Special Economic Zone
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80 mb-8 max-w-2xl font-body font-light tracking-wide leading-relaxed" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.7), 0 1px 4px rgba(0,0,0,0.5)" }}>
              Revitalizing the heart of Eurasia as a global hub for trade and economic growth.
            </p>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs text-muted-foreground tracking-widest uppercase">Scroll</span>
          <div className="w-px h-8 bg-primary/40 animate-pulse" />
        </div>
      </div>

      {/* Stats */}
      <section className="section-padding bg-card">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {stats.map((stat, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="stat-card text-center">
                  <div className="text-2xl md:text-3xl font-display font-bold text-primary mb-1">{stat.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left">
            <p className="text-primary font-display text-sm tracking-[0.3em] uppercase mb-4">About the Project</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              New Silkway Special Economic Zone
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Once a crossroads of ancient commerce, Central Asia is being redefined as a modern gateway—strategically positioned to serve as a high-capacity hub for global trade, logistics, and investment. The New Silkway SEZ integrates critical infrastructure with international access, enabling a new era of connectivity across continents.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Leveraging the growing influx of economic development in the region, New Silkway is establishing an advanced hub for global trade & commerce. Strategically located in the heart of Uzbekistan, the SEZ will enable both regional and global actors to benefit from this newfound momentum by using its ecosystem.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Development & investment opportunities ranging across transportation networks, data centers, warehousing, energy farms, finance, medical research, hospitality, and revolve around a state-of-the-art dry port, logistics hub, and business centers, all aimed at streamlining international trade and fostering economic growth. The SEZ will enable a world-class environment for global business in Central Asia, while enhancing Uzbekistan's position as a key player in driving regional economic progress and sustainable growth.
            </p>
            <Link to="/about/nsi" className="inline-flex items-center gap-2 text-primary hover:text-gold-light transition-colors font-display text-sm tracking-wider uppercase">
              Learn More About New Silkway <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
          <ScrollReveal direction="right" delay={200}>
            <div className="relative">
              <img src={financialImg} alt="New Silkway Financial Center" className="rounded-lg w-full aspect-[4/3] object-cover" width={729} height={417} loading="lazy" />
            <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-lg hidden md:block">
              <div className="text-3xl font-display font-bold">2,000+</div>
              <div className="text-sm">Hectares of Development</div>
            </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Dry Port Feature Section */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left">
            <div className="relative">
              <img src={dryPortImg} alt="New Silkway Inland Dry Port" className="rounded-lg w-full aspect-[4/3] object-cover" width={735} height={417} loading="lazy" />
              <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-6 rounded-lg hidden md:block">
                <div className="text-3xl font-display font-bold">600 ha</div>
                <div className="text-sm">Largest Zone in the SEZ</div>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="right" delay={200}>
            <p className="text-primary font-display text-sm tracking-[0.3em] uppercase mb-4">Anchor Development</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              Inland Dry Port — The Cornerstone
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Spanning 600 hectares, the New Silkway Inland Dry Port is the single largest component of the SEZ and its economic engine. 
              With a capacity of 8 million TEU per year and a dedicated rail corridor feeding directly into the facility, 
              it connects Central Asia to global maritime trade routes across China, Europe, Russia, and South Asia.
            </p>
            <div className="flex items-center gap-3 mb-6 text-primary">
              <TrainFront className="w-5 h-5" />
              <span className="text-sm font-display tracking-wider uppercase">Dedicated Rail Line Directly Into the Port</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="stat-card text-center">
                <div className="text-xl font-display font-bold text-primary">8M TEU</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Annual Capacity</div>
              </div>
              <div className="stat-card text-center">
                <div className="text-xl font-display font-bold text-primary">600 ha</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Footprint</div>
              </div>
            </div>
            <Link to="/development/dry-port" className="inline-flex items-center gap-2 text-primary hover:text-gold-light transition-colors font-display text-sm tracking-wider uppercase">
              Explore the Dry Port <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Parallax Mid */}
      <div ref={midSection.ref} className="relative h-[50vh] overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${solarImg})`, transform: `translateY(${midSection.offset}px)`, willChange: "transform" }}
        />
        <div className="absolute inset-0 overlay-dark" />
        <div className="relative z-10 text-center px-6">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4">Sustainable by Design</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Powered by a 100 MW solar plant, the New Silkway SEZ is committed to environmental responsibility and sustainable growth.
          </p>
        </div>
      </div>

      {/* Development Sectors */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <p className="text-primary font-display text-sm tracking-[0.3em] uppercase mb-4">Development Opportunities</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">An Integrated Economic Ecosystem</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map((sector, i) => (
              <ScrollReveal key={i} delay={(i % 3) * 150}>
                <Link to={sector.path} className="dev-card group aspect-[4/3]">
                <img src={sector.img} alt={sector.label} className="w-full h-full object-cover" loading="lazy" width={376} height={213} />
                <div className="dev-card-overlay">
                  <sector.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="text-lg font-display font-semibold text-foreground mb-1">{sector.label}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{sector.desc}</p>
                </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-card">
        <ScrollReveal>
          <div className="max-w-[1400px] mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              Ready to Be Part of Something Extraordinary?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Discover investment opportunities in one of Central Asia's most ambitious economic development projects.
            </p>
          <Link
            to="/sez/investment"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-display text-sm font-semibold tracking-wider uppercase rounded-sm hover:bg-gold-dark transition-colors"
          >
            Explore Opportunities <ArrowRight className="w-4 h-4" />
          </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Index;
